import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Pagination.css';

function Pagination({
    page,
    totalPages,
    limit,
    onPageChange,
    onLimitChange,
    storageKey = 'rova_default_limit',
    pageSizeOptions = [10, 20, 50, 100]
}) {
    // Load persisted limit on mount, or fallback to default prop limit
    useEffect(() => {
        const savedLimit = localStorage.getItem(storageKey);
        if (savedLimit) {
            const parsedLimit = parseInt(savedLimit, 10);
            if (parsedLimit && parsedLimit !== limit) {
                onLimitChange(parsedLimit);
            }
        } else {
            // Persist the default limit to storage if not set yet
            localStorage.setItem(storageKey, limit.toString());
        }
    }, [storageKey]);

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        localStorage.setItem(storageKey, newLimit.toString());
        onLimitChange(newLimit);
    };

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always include page 1
            pages.push(1);

            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);

            if (start > 2) {
                pages.push('ellipsis-start');
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (end < totalPages - 1) {
                pages.push('ellipsis-end');
            }

            // Always include last page
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="rova-pagination-container">
            <div className="rova-pagination-limit-selector">
                <label htmlFor={`${storageKey}-limit-select`}>Show</label>
                <select
                    id={`${storageKey}-limit-select`}
                    className="rova-pagination-select"
                    value={limit}
                    onChange={handleLimitChange}
                >
                    {pageSizeOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                <span>records per page</span>
            </div>

            {totalPages > 1 && (
                <div className="rova-pagination-controls">
                    <button
                        type="button"
                        className="rova-pagination-btn"
                        onClick={() => onPageChange(page - 1)}
                        disabled={page === 1}
                    >
                        <ChevronLeft size={14} />
                    </button>

                    <div className="rova-pagination-pages">
                        {getPageNumbers().map((p, idx) => {
                            if (p === 'ellipsis-start' || p === 'ellipsis-end') {
                                return (
                                    <span key={`ellipsis-${idx}`} className="rova-pagination-ellipsis">
                                        ...
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={`page-${p}`}
                                    type="button"
                                    className={`rova-pagination-page-btn ${page === p ? 'active' : ''}`}
                                    onClick={() => onPageChange(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        className="rova-pagination-btn"
                        onClick={() => onPageChange(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default Pagination;
